// El botón "Atajos" de la timeline y su panel — ADR-2026-11-03-los-atajos-se-ven.
//
// Imanol y Nacho, 3/11: quien no sabe que S divide o que las flechas mueven el
// cursor no lo va a descubrir nunca. El panel muestra los atajos del sistema del
// usuario (⌘ en Mac, Ctrl en Windows) y deja ver los del otro.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Keyboard, X } from 'lucide-react';
import { useAperturaAnimada } from '../../hooks/useAperturaAnimada';
import { ATAJOS_DEL_ESTUDIO, sistemaDelUsuario, type Sistema } from './atajosDelEstudio';

const ANCHO = 320;

export function BotonDeAtajos() {
  const [abierto, setAbierto] = useState(false);
  const [sistema, setSistema] = useState<Sistema>(() =>
    sistemaDelUsuario(typeof navigator === 'undefined' ? undefined : navigator),
  );
  const botonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [lugar, setLugar] = useState<{ left: number; bottom: number } | null>(null);
  const { montado, visible } = useAperturaAnimada(abierto, 150);

  // El panel va por portal al body (la tarjeta de la timeline recorta lo que se
  // sale) y se ubica arriba del botón, sin salirse de la pantalla.
  useLayoutEffect(() => {
    if (!abierto || !botonRef.current) return;
    const r = botonRef.current.getBoundingClientRect();
    const left = Math.max(16, Math.min(window.innerWidth - ANCHO - 16, r.right - ANCHO));
    setLugar({ left, bottom: window.innerHeight - r.top + 8 });
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || botonRef.current?.contains(t)) return;
      setAbierto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('pointerdown', fuera);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      window.removeEventListener('keydown', esc);
    };
  }, [abierto]);

  return (
    <>
      <button
        ref={botonRef}
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        className="h-7 px-2.5 flex items-center gap-1.5 whitespace-nowrap rounded-md text-[12px] font-semibold hover:bg-black/[0.04] transition-colors"
        style={{ color: 'rgb(var(--brand-black-rgb) / 0.8)' }}
        title="Ver los atajos de teclado"
      >
        <Keyboard className="w-3.5 h-3.5" aria-hidden="true" />
        Atajos
      </button>
      {montado &&
        lugar &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Atajos de teclado"
            className={`fixed z-[200] rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/[0.06] transition-[opacity,transform,filter] ease-out ${
              visible
                ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0'
                : abierto
                  ? 'opacity-0 duration-300 motion-safe:translate-y-3 motion-safe:blur-[4px]'
                  : 'opacity-0 duration-150 motion-safe:-translate-y-3 motion-safe:blur-[4px]'
            }`}
            style={{ left: lugar.left, bottom: lugar.bottom, width: ANCHO }}
          >
            <div className="flex items-center justify-between gap-2 pb-2">
              <p className="text-[13px] font-semibold text-brand-black">Atajos</p>
              <div className="flex items-center gap-1">
                <div className="flex rounded-md bg-black/[0.04] p-0.5">
                  {(['mac', 'windows'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSistema(s)}
                      className={`rounded-sm px-2 py-0.5 text-[11.5px] font-semibold transition-colors ${
                        sistema === s ? 'bg-white text-brand-black shadow-sm' : 'text-brand-black/55'
                      }`}
                    >
                      {s === 'mac' ? 'Mac' : 'Windows'}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setAbierto(false)}
                  className="rounded-sm p-1 text-brand-black/50 transition-colors hover:bg-black/[0.04]"
                  aria-label="Cerrar"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {ATAJOS_DEL_ESTUDIO.map(({ grupo, atajos }) => (
                <div key={grupo}>
                  <p className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-brand-black/45">
                    {grupo}
                  </p>
                  <ul className="space-y-1">
                    {atajos.map((a) => (
                      <li key={a.que} className="flex items-center justify-between gap-3 text-[12.5px]">
                        <span className="text-brand-black/80">{a.que}</span>
                        <span className="flex shrink-0 gap-1">
                          {a[sistema].map((t) => (
                            <kbd
                              key={t}
                              className="rounded-sm bg-black/[0.05] px-1.5 py-0.5 font-sans text-[11.5px] font-semibold text-brand-black/75"
                            >
                              {t}
                            </kbd>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
